import { useRef, useState, useEffect } from 'react';

import { IconPlus, IconTrash, IconLoader2 } from '@tabler/icons-react';

import { VALIDATION_MESSAGES } from '@/constants/constant';
import { useFormatText } from '@/hooks/useFormatText';

interface IProfileImagePickerProps {
  value?: File;
  onChange: (file: File | undefined) => void;
  isLoading?: boolean;
}

export const ProfileImagePicker = ({
  value,
  onChange,
  isLoading,
}: IProfileImagePickerProps) => {
  const preview = value ? URL.createObjectURL(value) : null;
  const inputRef = useRef<HTMLInputElement>(null);
  const [hovered, setHovered] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { formatText } = useFormatText();

  useEffect(() => {
    if (!isLoading) {
      setHovered(false);
    }
  }, [isLoading, value]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setError(formatText(VALIDATION_MESSAGES.profileImage.invalid));
        onChange(undefined);
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setError(formatText(VALIDATION_MESSAGES.profileImage.maxSize));
        onChange(undefined);
        return;
      }
      onChange(file);
    }
  };

  const handleRemove = () => {
    onChange(undefined);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium text-neutral-primary">
        {formatText('school.add-school-form.profile-image')}
      </label>

      <div
        className="relative w-16 h-16"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          hidden
          onChange={handleFileChange}
        />

        <div
          className="w-16 h-16 rounded-full border border-dashed border-neutral-secondary bg-neutral-light-200 flex items-center justify-center overflow-hidden cursor-pointer"
          onClick={() => !preview && inputRef.current?.click()}
        >
          {isLoading ? (
            <IconLoader2
              className="animate-spin text-neutral-secondary"
              size={24}
            />
          ) : preview ? (
            <>
              <img
                src={preview}
                alt="Profile"
                className="w-full h-full object-cover"
              />
              {hovered && (
                <button
                  type="button"
                  className="absolute inset-0 flex items-center justify-center bg-black opacity-30 rounded-full cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove();
                  }}
                  aria-label="Remove image"
                >
                  <IconTrash className="text-white" />
                </button>
              )}
            </>
          ) : (
            <IconPlus className="text-neutral-hover" size={24} />
          )}
        </div>
      </div>
      {error && (
        <p className="text-sm font-normal text-error-default">{error}</p>
      )}
    </div>
  );
};
